import React, {useEffect, useState} from "react";
import {Typography} from "antd";
import {useTranslation} from "react-i18next";
import {buildAuthorizedFileFetchOptions} from "../../../fileDownloadUrl";

type Props = {
    eventId: number;
    participantId: number;
    codeId: number;
    authToken?: string;
};

// The readable registration code under the QR image, for reading out or typing in when scanning fails.
// Codes issued before the code was stored have none, and then nothing is shown.
export default function ParticipantRegistrationCodeText({eventId, participantId, codeId, authToken}: Props) {
    const {t} = useTranslation();
    const [code, setCode] = useState<string>();

    useEffect(() => {
        const controller = new AbortController();
        setCode(undefined);

        const load = async () => {
            try {
                const response = await fetch(
                    `/api/events/${eventId}/participants/${participantId}/registration-codes/${codeId}/code`,
                    {...buildAuthorizedFileFetchOptions(authToken), cache: "no-store", signal: controller.signal}
                );
                if (!response.ok) return;
                const body = await response.json() as {code?: string};
                if (!controller.signal.aborted && body.code) setCode(body.code);
            } catch {
                // The code is optional next to the QR image; failures leave it hidden.
            }
        };
        void load();

        return () => controller.abort();
    }, [eventId, participantId, codeId, authToken]);

    if (!code) {
        return null;
    }

    return (
        <Typography.Text>
            {t("registration-code", {defaultValue: "Registration code"})}:{" "}
            <Typography.Text strong copyable style={{fontFamily: "monospace", letterSpacing: 1}}>{code}</Typography.Text>
        </Typography.Text>
    );
}
